/**
 * Itinerary Domain Schemas
 *
 * Zod schemas for Itinerary entity validation and type inference.
 * Single source of truth for types AND validation rules.
 */
import { z } from "zod";
import { toTypedSchema } from "@vee-validate/zod";
import { ITINERARY_STATE_VALUES } from "./itinerary-state.config";
// =============================================================================
// ENUMS
// =============================================================================
/**
 * Itinerary state enum
 */
export const ItineraryStateSchema = z.enum([
    ...ITINERARY_STATE_VALUES,
]);
/**
 * Price type enum
 */
export const PriceTypeSchema = z.enum(["PERSON", "HOUR"]);
/**
 * Commission type enum
 */
export const CommissionTypeSchema = z.enum(["PERCENTAGE", "FIXED"]);
// =============================================================================
// BOOKING ITEM SCHEMAS
// =============================================================================
/**
 * Booking item input (for create/update)
 * Minimal required fields for API submission
 */
export const bookingItemSchema = z.object({
    service_id: z.number().min(1, "Debe seleccionar un servicio"),
    service_slot_id: z.number().nullable().optional(),
    start_datetime: z.string().min(1, "Fecha de inicio requerida"),
    end_datetime: z.string().min(1, "Fecha de fin requerida"),
    quantity_of_participants: z.number().min(1, "Mínimo 1 participante"),
    custom_field_values: z.record(z.unknown()).default({}),
    notes: z.string().optional().default(""),
});
/**
 * Booking item with all pricing fields (for internal calculations)
 */
export const bookingItemWithPricingSchema = bookingItemSchema.extend({
    // Pricing fields
    price_per_service: z.number().min(0).default(0),
    price_type: PriceTypeSchema.default("PERSON"),
    overflow_price: z.number().nullable().optional(),
    tax_rate: z.number().min(0).max(1).default(0.13),
    // Commission fields
    commission_amount: z.number().min(0).default(0),
    commission_type: CommissionTypeSchema.default("PERCENTAGE"),
    commission_total_value: z.number().min(0).default(0),
    // Calculated totals
    subtotal: z.number().min(0).default(0),
    total: z.number().min(0).default(0),
    // Additional fields
    services_amount: z.number().int().min(0).default(1),
    discount_percentage: z.number().min(0).max(100).default(0),
    discount_amount: z.number().min(0).default(0),
});
// =============================================================================
// CUSTOMER SCHEMA
// =============================================================================
/**
 * Customer in itinerary
 */
export const itineraryCustomerSchema = z.object({
    id: z.number(),
    name: z.string(),
    last_name: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    whatsapp_phone: z.string().optional(),
});
// =============================================================================
// ITINERARY FORM SCHEMA
// =============================================================================
/**
 * Itinerary form data (for create/update)
 * Note: quantity_of_participants is now per-booking-item, not global
 */
export const itineraryFormSchema = z.object({
    customer_id: z.number().min(1, "Debe seleccionar un cliente"),
    booking_items: z
        .array(bookingItemSchema)
        .min(1, "Debe agregar al menos un servicio"),
    tags: z.array(z.number()).default([]),
    state: ItineraryStateSchema.default("PENDING"),
});
/**
 * VeeValidate typed schema for form validation
 */
export const itineraryValidationSchema = toTypedSchema(itineraryFormSchema);
// =============================================================================
// ITINERARY ENTITY SCHEMA
// =============================================================================
/**
 * Slot itinerary (from API response)
 */
export const slotItinerarySchema = z.object({
    id: z.number(),
    booking_id: z.number().optional(),
    itinerary_id: z.number().optional(),
    service_id: z.number(),
    service_slot_id: z.number().nullable().optional(),
    start_datetime: z.string().optional(),
    end_datetime: z.string().optional(),
    service_slot: z
        .object({
        id: z.number().optional(),
        start_date: z.string(),
        end_date: z.string(),
    })
        .optional(),
    quantity_of_participants: z.number().default(0),
    custom_field_values: z.record(z.unknown()).default({}),
    notes: z.string().optional(),
    additional_information_snapshot: z.string().nullable().optional(),
    subtotal: z.number().optional(),
    total_amount: z.number().optional(),
    service: z
        .object({
        id: z.number(),
        name: z.string(),
        description: z.string().optional(),
        duration: z.number().optional(),
        tax_rate: z.number().optional(),
        provider: z
            .object({
            id: z.number(),
            name: z.string(),
        })
            .optional(),
        service_type: z
            .object({
            id: z.number(),
            name: z.string(),
            slug: z.string().optional(),
            custom_fields_schema: z.array(z.unknown()).optional(),
        })
            .nullable()
            .optional(),
    })
        .optional(),
});
/**
 * Full itinerary entity (from API response)
 */
export const itinerarySchema = z.object({
    id: z.number(),
    code: z.string(),
    customer_id: z.number(),
    quantity_of_participants: z.number().default(0),
    state: ItineraryStateSchema,
    subtotal: z.number().optional(),
    commission_amount: z.number().default(0),
    tax_amount: z.number().optional(),
    total: z.number().optional(),
    creator: z
        .object({
        id: z.string(),
        assigned_person: z.string().nullable(),
        company_name: z.string().nullable(),
    })
        .nullable(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    customer: itineraryCustomerSchema.optional(),
    slots_itineraries: z.array(slotItinerarySchema).optional(),
    // Legacy field (deprecated)
    itinerary_tour: z.array(z.unknown()).optional(),
});
// =============================================================================
// DEFAULTS
// =============================================================================
/**
 * Default booking item for new entries
 */
export const defaultBookingItem = {
    service_id: 0,
    service_slot_id: null,
    start_datetime: "",
    end_datetime: "",
    quantity_of_participants: 0,
    custom_field_values: {},
    notes: "",
};
/**
 * Default booking item with pricing
 */
export const defaultBookingItemWithPricing = {
    ...defaultBookingItem,
    notes: "",
    price_per_service: 0,
    price_type: "PERSON",
    overflow_price: null,
    tax_rate: 0.13,
    commission_amount: 0,
    commission_type: "PERCENTAGE",
    commission_total_value: 0,
    subtotal: 0,
    total: 0,
    services_amount: 1,
    discount_percentage: 0,
    discount_amount: 0,
};
/**
 * Default itinerary form data
 */
export const defaultItineraryForm = {
    customer_id: 0,
    booking_items: [],
    tags: [],
    state: "PENDING",
};
